// AssetList.js - Displays assets in Clean Modern table format
import React from 'react';
import './AssetList.css';
import DriftStatusBadge from '../DriftStatusBadge';
import { HelpIcon } from '../common';
import { wafVendorLabel } from '../WafBlockingBanner';

// PRD-56 §5.2C: build a tooltip string for assets where the latest scan
// found WAF challenge-blocking on more than half the probed URLs. Used as a
// `title=` line on the existing risk-score badge — no new column.
// Stability layer: prefer Display fields (miss-counter-stable); require
// sample >= 10 to suppress small-N noise.
function buildRiskTooltip(asset) {
    const fraction = typeof asset.wafBlockingDisplayFraction === 'number'
        ? asset.wafBlockingDisplayFraction
        : (typeof asset.wafBlockingFraction === 'number' ? asset.wafBlockingFraction : 0);
    const sample = asset.wafBlockingSampleSize || 0;
    if (fraction <= 0.5 || sample < 10) return undefined;
    const vendor = wafVendorLabel(asset.wafBlockingDisplayType || asset.wafBlockingType);
    const pct = Math.round(fraction * 100);
    return `⚠ ${vendor} WAF blocked ${pct}% of probes — coverage limited`;
}

function AssetList({ assets, onDelete, onScanNow, onViewDetails, deletingAssetId }) {
    // Get asset type icon
    const getAssetIcon = (type) => {
        switch (type) {
            case 'domain': return '🌐';
            case 'ip': return '📍';
            case 'cidr': return '🔲';
            default: return '📡';
        }
    };

    // Format relative time for last scan
    const getRelativeTime = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;

        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days === 1) return '1d ago';
        return `${days}d ago`;
    };

    // Get risk status based on score or findings
    const getRiskStatus = (asset) => {
        const hasVulns = (asset.criticalCount || 0) + (asset.highCount || 0) > 0;
        if (asset.riskScore < 50 || hasVulns) return 'at-risk';
        if (asset.riskScore < 80) return 'warning';
        return 'healthy';
    };

    return (
        <div className="cm-table-wrapper">
            <table className="cm-table asset-table">
                <thead>
                    <tr>
                        <th className="col-status"></th>
                        <th className="col-asset">Asset</th>
                        <th className="col-score">Score<HelpIcon content="Risk is calculated from open vulnerabilities, exposed services, and configuration weaknesses." category="risk" /></th>
                        <th className="col-drift">Drift<HelpIcon content="Shows whether the asset has changed since its baseline. Click an asset to see what changed." category="context" /></th>
                        <th className="col-findings">Findings</th>
                        <th className="col-subs">Subs</th>
                        <th className="col-ports">Ports</th>
                        <th className="col-ips">IPs</th>
                        <th className="col-scan">Last Scan</th>
                        <th className="col-actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {assets.map((asset) => {
                        const riskStatus = getRiskStatus(asset);
                        const lastScan = asset.lastVulnScanAt || asset.lastDiscoveryAt || asset.lastBaselineAt;
                        const isDeleting = deletingAssetId === asset.assetId;
                        const isBlocked = !!deletingAssetId;

                        return (
                            <tr
                                key={asset.assetId}
                                className={`asset-row ${riskStatus}${isDeleting ? ' deleting' : ''}`}
                                onClick={() => !isBlocked && onViewDetails(asset.assetId)}
                            >
                                {/* Status Dot */}
                                <td className="col-status" data-label="">
                                    <span
                                        className={`cm-status-dot ${riskStatus}`}
                                        title={riskStatus === 'at-risk' ? 'At Risk' : riskStatus === 'warning' ? 'Warning' : 'Healthy'}
                                    />
                                </td>

                                {/* Asset Name */}
                                <td className="col-asset" data-label="Name">
                                    <div className="asset-cell">
                                        <span className="asset-icon">{getAssetIcon(asset.assetType)}</span>
                                        <span className="asset-name">{asset.value}</span>
                                    </div>
                                </td>

                                {/* Risk Score */}
                                <td className="col-score" data-label="Score">
                                    <span
                                        className={`score-value ${riskStatus}`}
                                        title={buildRiskTooltip(asset)}
                                    >
                                        {asset.riskScore || '—'}
                                    </span>
                                </td>

                                {/* Drift Status */}
                                <td className="col-drift" data-label="Drift">
                                    <DriftStatusBadge
                                        status={asset.driftStatus}
                                        size="small"
                                        showLabel={true}
                                    />
                                </td>

                                {/* Findings Summary */}
                                <td className="col-findings" data-label="Findings">
                                    <div className="findings-badges">
                                        {(asset.criticalCount || 0) > 0 && (
                                            <span
                                                className="cm-severity-badge critical"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onViewDetails(asset.assetId, 'vulnerabilities-critical');
                                                }}
                                            >
                                                {asset.criticalCount}C
                                            </span>
                                        )}
                                        {(asset.highCount || 0) > 0 && (
                                            <span
                                                className="cm-severity-badge high"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onViewDetails(asset.assetId, 'vulnerabilities-high');
                                                }}
                                            >
                                                {asset.highCount}H
                                            </span>
                                        )}
                                        {(asset.mediumCount || 0) > 0 && (
                                            <span
                                                className="cm-severity-badge medium"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onViewDetails(asset.assetId, 'vulnerabilities-medium');
                                                }}
                                            >
                                                {asset.mediumCount}M
                                            </span>
                                        )}
                                        {(asset.criticalCount || 0) === 0 &&
                                         (asset.highCount || 0) === 0 &&
                                         (asset.mediumCount || 0) === 0 && (
                                            <span className="no-findings">—</span>
                                        )}
                                    </div>
                                </td>

                                {/* Subdomains */}
                                <td className="col-subs" data-label="Subs">
                                    <span
                                        className="stat-link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewDetails(asset.assetId, 'subdomains');
                                        }}
                                    >
                                        {asset.liveSubdomains || 0}
                                    </span>
                                </td>

                                {/* Ports */}
                                <td className="col-ports" data-label="Ports">
                                    <span
                                        className="stat-link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewDetails(asset.assetId, 'ports');
                                        }}
                                    >
                                        {asset.openPortCount || 0}
                                    </span>
                                </td>

                                {/* IPs */}
                                <td className="col-ips" data-label="IPs">
                                    <span
                                        className="stat-link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewDetails(asset.assetId, 'ips');
                                        }}
                                    >
                                        {asset.ipCount || 0}
                                    </span>
                                </td>

                                {/* Last Scan */}
                                <td className="col-scan" data-label="Last Scan">
                                    <span className="scan-time">{getRelativeTime(lastScan)}</span>
                                </td>

                                {/* Actions */}
                                <td className="col-actions" data-label="Actions">
                                    <div className="action-buttons">
                                        <button
                                            className="cm-action-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onViewDetails(asset.assetId);
                                            }}
                                            title="View Details"
                                            disabled={isBlocked}
                                        >
                                            View
                                        </button>
                                        <button
                                            className="cm-action-btn secondary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onScanNow(asset.assetId);
                                            }}
                                            title="Trigger Scan"
                                            disabled={isBlocked}
                                        >
                                            Scan
                                        </button>
                                        <button
                                            className="cm-action-btn danger"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isBlocked) onDelete(asset.assetId);
                                            }}
                                            title={isDeleting ? 'Deleting — please wait...' : 'Delete Asset'}
                                            disabled={isBlocked}
                                            style={isDeleting ? { opacity: 0.6, cursor: 'wait' } : {}}
                                        >
                                            {isDeleting ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Legend */}
            <div className="table-legend">
                <span className="legend-item">
                    <span className="cm-status-dot at-risk"></span> At Risk
                </span>
                <span className="legend-item">
                    <span className="cm-status-dot warning"></span> Warning
                </span>
                <span className="legend-item">
                    <span className="cm-status-dot healthy"></span> Healthy
                </span>
            </div>
        </div>
    );
}

export default AssetList;
